/**
 * CCS Inventory Builder — 4-step supply list wizard
 *
 * URL: /pages/build-your-list
 * Spec: docs/superpowers/specs/2026-04-15-inventory-builder-design.md (v3)
 *
 * Architecture:
 *   - All 4 steps rendered as hidden divs; JS shows/hides on navigation
 *   - Product cards rendered by Liquid with data attributes; detail fetched via /products/{handle}.json
 *   - State persisted to localStorage (ccsBuilder key, 7-day TTL)
 *   - Submission via n8n webhook with /contact fallback
 *   - Abandonment tracking via navigator.sendBeacon
 *
 * ES5 only — no let/const, no arrows, no template literals.
 */
(function () {
  'use strict';

  /* ─── Constants ─────────────────────────────────────────────── */

  var STORAGE_KEY = 'ccsBuilder';
  var STORAGE_TTL = 7 * 24 * 60 * 60 * 1000; /* 7 days in ms */
  var MIN_ORDER   = 15000; /* $150 in cents */
  var DELIVERY_FEE = 1499; /* $14.99 in cents */
  var HST_RATE    = 0.13;
  var N8N_WEBHOOK = ''; /* TODO: set before launch */
  var N8N_TIMEOUT = 15000; /* 15s for cold start */

  /* ─── GTA FSA Allow-List ────────────────────────────────────── */

  var GTA_FSAS = [
    'M1A','M1B','M1C','M1E','M1G','M1H','M1J','M1K','M1L','M1M','M1N','M1P','M1R','M1S','M1T','M1V','M1W','M1X',
    'M2H','M2J','M2K','M2L','M2M','M2N','M2P','M2R',
    'M3A','M3B','M3C','M3H','M3J','M3K','M3L','M3M','M3N',
    'M4A','M4B','M4C','M4E','M4G','M4H','M4J','M4K','M4L','M4M','M4N','M4P','M4R','M4S','M4T','M4V','M4W','M4X','M4Y',
    'M5A','M5B','M5C','M5E','M5G','M5H','M5J','M5K','M5L','M5M','M5N','M5P','M5R','M5S','M5T','M5V','M5W','M5X',
    'M6A','M6B','M6C','M6E','M6G','M6H','M6J','M6K','M6L','M6M','M6N','M6P','M6R','M6S',
    'M8V','M8W','M8X','M8Y','M8Z','M9A','M9B','M9C','M9L','M9M','M9N','M9P','M9R','M9V','M9W',
    'L4T','L4W','L4X','L4Y','L4Z',
    'L5A','L5B','L5C','L5E','L5G','L5H','L5J','L5K','L5L','L5M','L5N','L5P','L5R','L5S','L5T','L5V','L5W',
    'L6P','L6R','L6S','L6T','L6V','L6W','L6X','L6Y','L6Z','L7A',
    'L3P','L3R','L3S','L3T','L4B','L4C','L4E','L4S',
    'L6B','L6C','L6E','L6G',
    'L4H','L4J','L4K','L4L',
    'L6H','L6J','L6K','L6L','L6M',
    'L1S','L1T','L1V','L1W','L1X','L1Z',
    'L1L','L1M','L1N','L1P','L1R',
    'L1G','L1H','L1J','L1K'
  ];

  /* ─── Facility → rec tag mapping ───────────────────────────── */
  var FACILITY_TAG_MAP = {
    office: 'rec-office',
    condo: 'rec-condo',
    restaurant: 'rec-restaurant',
    school: 'rec-school',
    gym: 'rec-gym',
    medical: 'rec-medical',
    retail: 'rec-retail',
    cleaning_company: 'rec-cleaning-company',
    other: 'rec-other'
  };

  /* ─── State ─────────────────────────────────────────────────── */

  var state = {
    step: 1,
    postalCode: '',
    inGta: false,
    facilityType: '',
    facilitySize: '',
    bscSubTypes: [],
    email: '',
    caslConsent: false,
    products: [],   /* [{ handle, title, variantId, price, quantity, imageUrl, category, description, unitsPerCase }] */
    frequency: 'weekly',
    submitted: false
  };

  var productCache = {}; /* handle → full product JSON from AJAX */
  var selectedHandle = '';

  /* ─── Helpers ───────────────────────────────────────────────── */

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function $(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function $$(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  function announce(msg) {
    var el = document.getElementById('ccs-ib-live');
    if (el) { el.textContent = msg; }
  }

  function getSubtotal() {
    var total = 0;
    for (var i = 0; i < state.products.length; i++) {
      total += state.products[i].price * state.products[i].quantity;
    }
    return total;
  }

  function getDeliveryDay(/* postalCode */) {
    /* MVP: all zones = Thursday. Update this function for zone-based routing. */
    return { day: 'Thursday', cutoff: 'Wednesday' };
  }

  function getNextDeliveryDate(dayName) {
    var days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    var target = days.indexOf(dayName);
    var now = new Date();
    var diff = (target - now.getDay() + 7) % 7;
    if (diff === 0) diff = 7; /* next week if today is the day */
    var next = new Date(now.getTime() + diff * 86400000);
    var months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    return months[next.getMonth()] + ' ' + next.getDate() + ', ' + next.getFullYear();
  }

  /* ─── localStorage ──────────────────────────────────────────── */

  function saveState() {
    try {
      var data = {
        step: state.step,
        postalCode: state.postalCode,
        facilityType: state.facilityType,
        facilitySize: state.facilitySize,
        bscSubTypes: state.bscSubTypes,
        email: state.email,
        caslConsent: state.caslConsent,
        products: state.products.map(function (p) {
          return { handle: p.handle, variantId: p.variantId, quantity: p.quantity };
        }),
        frequency: state.frequency,
        timestamp: Date.now()
      };
      window.ccsLS.set(STORAGE_KEY, JSON.stringify(data));
    } catch (e) { /* localStorage unavailable */ }
  }

  function loadState() {
    try {
      var raw = window.ccsLS.get(STORAGE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (Date.now() - data.timestamp > STORAGE_TTL) {
        window.ccsLS.del(STORAGE_KEY);
        return null;
      }
      return data;
    } catch (e) { return null; }
  }

  function clearState() {
    try { window.ccsLS.del(STORAGE_KEY); } catch (e) {}
  }

  /* ─── Step Navigation ───────────────────────────────────────── */

  function goToStep(n) {
    var steps = $$('.ccs-ib-step');
    for (var i = 0; i < steps.length; i++) {
      steps[i].style.display = 'none';
    }
    var target = document.querySelector('.ccs-ib-step[data-step="' + n + '"]');
    if (target) {
      target.style.display = 'block';
      var heading = target.querySelector('h2');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus();
      }
    }

    /* Update labeled progress stepper */
    var stepNum = parseInt(n, 10) || 4;
    var steps = $$('.ccs-ib-progress-step');
    for (var j = 0; j < steps.length; j++) {
      var sn = parseInt(steps[j].getAttribute('data-step'), 10);
      steps[j].classList.remove('active', 'completed');
      if (sn === stepNum) {
        steps[j].classList.add('active');
      } else if (sn < stepNum) {
        steps[j].classList.add('completed');
        var dot = steps[j].querySelector('.ccs-ib-progress-dot');
        if (dot) dot.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>';
      } else {
        /* Reset future steps back to numbers */
        var futureDot = steps[j].querySelector('.ccs-ib-progress-dot');
        if (futureDot) futureDot.textContent = sn;
      }
    }
    /* Update fill line width: 0% at step 1, 33% at step 2, 66% at step 3, 100% at step 4 */
    var fill = document.getElementById('ccs-ib-progress-fill');
    if (fill) {
      var pct = Math.max(0, (stepNum - 1) / 3 * 100);
      fill.style.width = 'calc(' + pct + '% - ' + (80 - pct * 0.8) + 'px)';
    }
    var bar = $('.ccs-ib-progress');
    if (bar) {
      bar.setAttribute('aria-valuenow', stepNum);
    }

    /* Announce for screen readers */
    var titles = { 1: 'Tell us about your facility', 2: 'Your recommended supplies', 3: 'Review your list', 4: 'Complete your order' };
    if (titles[stepNum]) {
      announce('Step ' + stepNum + ' of 4: ' + titles[stepNum]);
    }

    /* Update state and localStorage */
    state.step = n;
    saveState();

    /* Push history state for back button */
    if (history.pushState) {
      history.pushState({ step: n }, '', '');
    }

    /* Hide hero on steps 2+ , hide progress on confirmation */
    var hero = document.querySelector('.ccs-ib-hero');
    var progress = document.getElementById('ccs-ib-progress');
    if (hero) hero.style.display = (stepNum === 1 || n === 1) ? '' : 'none';
    if (progress) progress.style.display = (n === 'confirm') ? 'none' : '';

    /* Show drawer only on Step 2 */
    var drawer = document.getElementById('ccs-ib-drawer');
    if (drawer) {
      if (n === 2 || n === '2') {
        drawer.classList.add('visible');
      } else {
        drawer.classList.remove('visible', 'expanded');
      }
    }

    /* Scroll to top */
    window.scrollTo(0, 0);
  }

  /* ─── Analytics ─────────────────────────────────────────────── */

  function track(event, data) {
    if (window.dataLayer) {
      var payload = { event: 'ccs_builder_' + event };
      for (var key in data) {
        if (data.hasOwnProperty(key)) {
          payload[key] = data[key];
        }
      }
      window.dataLayer.push(payload);
    }
  }

  /* ─── Abandonment Recovery ──────────────────────────────────── */

  function setupAbandonment() {
    window.addEventListener('beforeunload', function () {
      if (state.products.length > 0 && !state.submitted) {
        var payload = new URLSearchParams({
          'form_type': 'contact',
          'contact[email]': state.email || 'anonymous-builder@cleancaresupplies.ca',
          'contact[body]': JSON.stringify({
            type: 'builder_abandoned',
            step: state.step,
            item_count: state.products.length,
            has_email: !!state.email,
            facility_type: state.facilityType,
            timestamp: Date.now()
          }),
          'contact[tags]': 'builder-abandoned'
        });
        navigator.sendBeacon('/contact', payload);
        track('builder_abandoned', {
          step_number: state.step,
          item_count: state.products.length,
          has_email: !!state.email
        });
      }
    });
  }

  /* ─── Back Button Support ───────────────────────────────────── */

  function setupHistory() {
    window.addEventListener('popstate', function (e) {
      if (e.state && e.state.step) {
        goToStep(e.state.step);
      }
    });
  }

  /* ─── Init ──────────────────────────────────────────────────── */

  function init() {
    setupAbandonment();
    setupHistory();
    initStep1();
    initStep2();
    initStep3();
    initStep4();
    track('builder_started', { source: getUtmSource() });

    /* Check for resume */
    var saved = loadState();
    if (saved && saved.step > 1 && saved.products && saved.products.length > 0) {
      showResumePrompt(saved);
    }
  }

  function getUtmSource() {
    var params = new URLSearchParams(window.location.search);
    return params.get('utm_source') || '';
  }

  /* ── Placeholder init functions (filled in by subsequent tasks) ── */
  function initStep1() {
    var postalInput = document.getElementById('ccs-ib-postal-input');
    var postalBadge = document.getElementById('ccs-ib-postal-badge');
    var postalError = document.getElementById('ccs-ib-postal-error');
    var facilityCards = $$('.ccs-ib-facility-card');
    var bscSub = document.getElementById('ccs-ib-bsc-sub');
    var sizeButtons = $$('.ccs-ib-size-btn');
    var sizeError = document.getElementById('ccs-ib-size-error');
    var cta = document.getElementById('ccs-ib-s1-cta');

    /* Postal code validation */
    function validatePostal() {
      var raw = postalInput.value.replace(/[\s-]/g, '').toUpperCase();
      postalBadge.className = 'ccs-ib-postal-badge';
      postalInput.classList.remove('valid-gta', 'valid-outside', 'valid-invalid');
      postalError.style.display = 'none';

      if (raw.length < 3) {
        state.postalCode = '';
        state.inGta = false;
        updateCtaState();
        return;
      }

      if (!/^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(raw) && raw.length >= 6) {
        postalBadge.textContent = 'Please enter a valid Canadian postal code';
        postalBadge.classList.add('invalid');
        postalInput.classList.add('valid-invalid');
        postalError.textContent = 'Please enter a valid Canadian postal code';
        postalError.style.display = 'block';
        state.postalCode = '';
        state.inGta = false;
        updateCtaState();
        return;
      }

      var fsa = raw.substring(0, 3);
      state.postalCode = raw;

      if (GTA_FSAS.indexOf(fsa) !== -1) {
        postalBadge.textContent = 'We deliver to your area — delivery included with orders over $150!';
        postalBadge.classList.add('gta');
        postalInput.classList.add('valid-gta');
        state.inGta = true;
        track('postal_code_entered', { postal_code: fsa, in_gta: true });
      } else {
        postalBadge.textContent = 'We ship Canada-wide via carrier — shipping rates apply at checkout.';
        postalBadge.classList.add('outside');
        postalInput.classList.add('valid-outside');
        state.inGta = false;
        track('postal_code_entered', { postal_code: fsa, in_gta: false });
      }
      updateCtaState();
      saveState();
    }

    /* Auto-format postal code: A1A 1A1 */
    postalInput.addEventListener('input', function () {
      var raw = postalInput.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      if (raw.length > 6) raw = raw.substring(0, 6);
      if (raw.length > 3) {
        postalInput.value = raw.substring(0, 3) + ' ' + raw.substring(3);
      } else {
        postalInput.value = raw;
      }
      validatePostal();
    });
    postalInput.addEventListener('blur', validatePostal);

    /* Facility type selection */
    function selectFacility(card) {
      for (var i = 0; i < facilityCards.length; i++) {
        facilityCards[i].setAttribute('aria-checked', 'false');
      }
      card.setAttribute('aria-checked', 'true');
      state.facilityType = card.getAttribute('data-type');

      /* Show/hide BSC sub-question */
      if (state.facilityType === 'cleaning_company') {
        bscSub.classList.add('visible');
      } else {
        bscSub.classList.remove('visible');
        state.bscSubTypes = [];
      }

      track('facility_selected', { facility_type: state.facilityType });
      updateCtaState();
      saveState();
    }

    for (var i = 0; i < facilityCards.length; i++) {
      facilityCards[i].addEventListener('click', function () { selectFacility(this); });
      facilityCards[i].addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          selectFacility(this);
        }
      });
    }

    /* BSC sub-type checkboxes */
    var bscChecks = $$('.ccs-ib-bsc-check input');
    for (var b = 0; b < bscChecks.length; b++) {
      bscChecks[b].addEventListener('change', function () {
        state.bscSubTypes = [];
        for (var j = 0; j < bscChecks.length; j++) {
          if (bscChecks[j].checked) {
            state.bscSubTypes.push(bscChecks[j].value);
          }
        }
        saveState();
      });
    }

    /* Size selection */
    function selectSize(btn) {
      for (var i = 0; i < sizeButtons.length; i++) {
        sizeButtons[i].setAttribute('aria-checked', 'false');
      }
      btn.setAttribute('aria-checked', 'true');
      state.facilitySize = btn.getAttribute('data-size');
      sizeError.style.display = 'none';
      updateCtaState();
      saveState();
    }

    for (var s = 0; s < sizeButtons.length; s++) {
      sizeButtons[s].addEventListener('click', function () { selectSize(this); });
    }

    /* CTA state */
    function updateCtaState() {
      var ready = state.postalCode && state.facilityType && state.facilitySize;
      if (ready) {
        cta.removeAttribute('aria-disabled');
        cta.disabled = false;
      } else {
        cta.setAttribute('aria-disabled', 'true');
        cta.disabled = true;
      }
    }

    /* CTA click */
    cta.addEventListener('click', function () {
      if (!state.postalCode) {
        postalInput.focus();
        return;
      }
      if (!state.facilityType) {
        facilityCards[0].focus();
        return;
      }
      if (!state.facilitySize) {
        sizeError.style.display = 'block';
        sizeButtons[0].focus();
        return;
      }

      /* Sort recommended products and show Step 2 */
      sortRecommended();
      updateDrawer();
      resetPanel();

      track('step_completed', { step_number: 1 });
      goToStep(2);
    });
  }
  /* ─── Step 2 Functions — Split-panel product browser ─────────── */

  /* Expose for inline onclick — back navigation */
  window.ccsBuilderBack = function (targetStep) {
    if (targetStep === 2 || targetStep === '2') {
      syncOnListBadges();
      updateDrawer();
      resetPanel();
    }
    goToStep(targetStep);
  };

  function initStep2() {
    var tabs = $$('.ccs-ib-tab');
    for (var t = 0; t < tabs.length; t++) {
      tabs[t].addEventListener('click', function () {
        for (var i = 0; i < tabs.length; i++) {
          tabs[i].setAttribute('aria-selected', 'false');
        }
        this.setAttribute('aria-selected', 'true');
        filterStep2(this.getAttribute('data-category').trim());
      });
    }

    var gridWrap = document.querySelector('.ccs-ib-s2-grid-wrap');
    if (gridWrap) {
      gridWrap.addEventListener('click', function (e) {
        var wrap = e.target.closest('.ccs-ib-s2-card-wrap');
        if (!wrap) return;
        e.preventDefault();
        selectProduct(wrap);
      });
    }

    var pdpDec = document.getElementById('ccs-ib-s2-pdp-dec');
    var pdpInc = document.getElementById('ccs-ib-s2-pdp-inc');
    var pdpQtyInput = document.getElementById('ccs-ib-s2-pdp-qty-input');
    if (pdpDec) {
      pdpDec.addEventListener('click', function () {
        var v = parseInt(pdpQtyInput.value, 10) || 1;
        if (v > 1) pdpQtyInput.value = v - 1;
      });
    }
    if (pdpInc) {
      pdpInc.addEventListener('click', function () {
        var v = parseInt(pdpQtyInput.value, 10) || 1;
        if (v < 99) pdpQtyInput.value = v + 1;
      });
    }

    var atlBtn = document.getElementById('ccs-ib-s2-pdp-atl');
    if (atlBtn) {
      atlBtn.addEventListener('click', function () { addSelectedToList(); });
    }

    /* Drawer event listeners */
    var drawerHandle = document.getElementById('ccs-ib-drawer-handle');
    if (drawerHandle) {
      drawerHandle.addEventListener('click', function (e) { e.stopPropagation(); toggleDrawer(); });
    }
    /* Prevent expanded content clicks from toggling */
    var drawerExpanded = document.getElementById('ccs-ib-drawer-expanded');
    if (drawerExpanded) {
      drawerExpanded.addEventListener('click', function (e) { e.stopPropagation(); });
    }
    var drawerCta = document.getElementById('ccs-ib-drawer-cta');
    if (drawerCta) {
      drawerCta.addEventListener('click', function () { goToReview(); });
    }
    var drawerReview = document.getElementById('ccs-ib-drawer-review');
    if (drawerReview) {
      drawerReview.addEventListener('click', function () { goToReview(); });
    }
    var drawerMore = document.getElementById('ccs-ib-drawer-more');
    if (drawerMore) {
      drawerMore.addEventListener('click', function () { toggleDrawer(); });
    }

    var sheetProductClose = document.getElementById('ccs-ib-sheet-product-close');
    var sheetListClose = document.getElementById('ccs-ib-sheet-list-close');
    var overlay = document.getElementById('ccs-ib-sheet-overlay');
    if (sheetProductClose) sheetProductClose.addEventListener('click', closeAllSheets);
    if (sheetListClose) sheetListClose.addEventListener('click', closeAllSheets);
    if (overlay) overlay.addEventListener('click', closeAllSheets);
  }

  function sortRecommended() {
    var recTag = FACILITY_TAG_MAP[state.facilityType] || 'rec-other';
    var recGrid = document.getElementById('ccs-ib-s2-rec-grid');
    var allGrid = document.getElementById('ccs-ib-s2-all-grid');
    var recHeading = document.getElementById('ccs-ib-s2-rec-heading');
    if (!recGrid || !allGrid) return;

    var typeNames = {
      office: 'Office Buildings', condo: 'Condos & Apartments',
      restaurant: 'Restaurants', school: 'Schools & Daycares',
      gym: 'Gyms & Fitness', medical: 'Medical & Dental',
      retail: 'Retail Stores', cleaning_company: 'Cleaning Companies',
      other: 'Your Facility'
    };
    if (recHeading) {
      recHeading.innerHTML = '<span class="ccs-ib-s2-rec-badge">Recommended</span> Recommended for ' + (typeNames[state.facilityType] || 'Your Facility');
    }

    var allCards = $$('.ccs-ib-s2-card-wrap');
    for (var i = 0; i < allCards.length; i++) {
      var card = allCards[i];
      var tags = (card.getAttribute('data-rec-tags') || '').split(',');
      if (tags.indexOf(recTag) !== -1) {
        recGrid.appendChild(card);
      } else {
        allGrid.appendChild(card);
      }
    }

    var recSection = document.getElementById('ccs-ib-s2-rec-section');
    if (recSection) {
      recSection.style.display = recGrid.children.length > 0 ? '' : 'none';
    }
    syncOnListBadges();
  }

  function filterStep2(category) {
    var allCards = $$('.ccs-ib-s2-card-wrap');
    var recVisible = 0;
    for (var i = 0; i < allCards.length; i++) {
      var cardType = (allCards[i].getAttribute('data-product-type') || '').trim();
      var matchesCategory = category === 'all' || cardType === category;
      allCards[i].style.display = matchesCategory ? '' : 'none';
      /* Count visible recommended cards */
      var recGrid = document.getElementById('ccs-ib-s2-rec-grid');
      if (matchesCategory && recGrid && recGrid.contains(allCards[i])) {
        recVisible++;
      }
    }
    /* Hide recommended section if no matches in current filter */
    var recSection = document.getElementById('ccs-ib-s2-rec-section');
    if (recSection) recSection.style.display = recVisible > 0 ? '' : 'none';

    var allHeading = document.getElementById('ccs-ib-s2-all-heading');
    if (allHeading) {
      allHeading.textContent = category === 'all' ? 'Browse All Products' : 'All ' + category;
    }
  }

  function selectProduct(cardWrap) {
    var handle = cardWrap.getAttribute('data-handle');
    selectedHandle = handle;

    var data = {
      handle: handle,
      title: cardWrap.getAttribute('data-title'),
      vendor: cardWrap.getAttribute('data-vendor'),
      price: parseInt(cardWrap.getAttribute('data-price'), 10),
      variantId: parseInt(cardWrap.getAttribute('data-variant-id'), 10),
      image: cardWrap.getAttribute('data-image'),
      productType: cardWrap.getAttribute('data-product-type'),
      variants: []
    };

    try {
      var rawVariants = cardWrap.getAttribute('data-variants');
      if (rawVariants) data.variants = JSON.parse(rawVariants);
    } catch (e) { /* silent */ }

    if (window.innerWidth < 768) {
      openProductSheet(data);
    } else {
      showPanelPDP(data);
    }

    fetchProductData(handle, function (full) {
      if (selectedHandle !== handle) return;
      if (window.innerWidth < 768) {
        updateSheetWithFullData(full);
      } else {
        updatePanelWithFullData(full);
      }
    });

    track('product_viewed', { product_handle: handle });
  }

  function showPanelPDP(data) {
    var placeholder = document.getElementById('ccs-ib-s2-panel-placeholder');
    var pdpEl = document.getElementById('ccs-ib-s2-panel-pdp');
    if (placeholder) placeholder.style.display = 'none';
    if (pdpEl) pdpEl.style.display = '';

    var brand = document.getElementById('ccs-ib-s2-pdp-brand');
    var title = document.getElementById('ccs-ib-s2-pdp-title');
    var price = document.getElementById('ccs-ib-s2-pdp-price');
    var carousel = document.getElementById('ccs-ib-s2-pdp-carousel');
    var variantsEl = document.getElementById('ccs-ib-s2-pdp-variants');
    var desc = document.getElementById('ccs-ib-s2-pdp-desc');
    var qtyInput = document.getElementById('ccs-ib-s2-pdp-qty-input');

    if (brand) brand.textContent = data.vendor || '';
    if (title) title.textContent = data.title || '';
    var firstVariantLabel = 'each';
    var firstVariantPrice = data.price;
    if (data.variants && data.variants.length > 0) {
      var fv = data.variants[0];
      firstVariantPrice = parseInt(fv.price, 10) || data.price;
      if (fv.title && fv.title !== 'Default Title' && fv.title !== 'Default') {
        firstVariantLabel = fv.title;
      }
    }
    if (price) price.innerHTML = formatMoney(firstVariantPrice) + ' <span class="unit">/ ' + firstVariantLabel + '</span>';
    if (carousel) carousel.innerHTML = data.image ? '<img src="' + data.image + '" alt="' + (data.title || '').replace(/"/g, '&amp;quot;') + '">' : '';
    if (desc) desc.innerHTML = '<p>Loading details...</p>';
    if (qtyInput) qtyInput.value = 1;

    if (variantsEl) {
      variantsEl.innerHTML = '';
      if (data.variants && data.variants.length > 1) {
        for (var v = 0; v < data.variants.length; v++) {
          var vr = data.variants[v];
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'ccs-ib-s2-pdp-variant-btn' + (v === 0 ? ' active' : '');
          btn.setAttribute('data-variant-id', vr.id);
          btn.setAttribute('data-variant-price', vr.price);
          btn.textContent = vr.title;
          btn.addEventListener('click', function () {
            var siblings = variantsEl.querySelectorAll('.ccs-ib-s2-pdp-variant-btn');
            for (var s = 0; s < siblings.length; s++) siblings[s].classList.remove('active');
            this.classList.add('active');
            var vPrice = parseInt(this.getAttribute('data-variant-price'), 10);
            if (price) price.innerHTML = formatMoney(vPrice) + ' <span class="unit">/ ' + this.textContent + '</span>';
          });
          variantsEl.appendChild(btn);
        }
      }
    }
  }

  function resetPanel() {
    var placeholder = document.getElementById('ccs-ib-s2-panel-placeholder');
    var pdpEl = document.getElementById('ccs-ib-s2-panel-pdp');
    if (placeholder) placeholder.style.display = '';
    if (pdpEl) pdpEl.style.display = 'none';
    selectedHandle = '';
  }

  function fetchProductData(handle, callback) {
    if (productCache[handle]) {
      callback(productCache[handle]);
      return;
    }
    fetch('/products/' + handle + '.json')
      .then(function (res) { return res.json(); })
      .then(function (data) {
        productCache[handle] = data.product;
        callback(data.product);
      })
      .catch(function () { /* silent */ });
  }

  function updatePanelWithFullData(product) {
    var desc = document.getElementById('ccs-ib-s2-pdp-desc');
    var carousel = document.getElementById('ccs-ib-s2-pdp-carousel');
    var stock = document.getElementById('ccs-ib-s2-pdp-stock');

    if (desc && product.body_html) {
      desc.innerHTML = product.body_html;
    }

    if (carousel && product.images && product.images.length > 0) {
      var html = '';
      for (var i = 0; i < product.images.length; i++) {
        var display = i === 0 ? '' : 'display:none;';
        html += '<img src="' + product.images[i].src.replace(/\.([^.]+)$/, '_600x600.$1') + '" alt="' + (product.title || '').replace(/"/g, '&amp;quot;') + '" style="' + display + '" data-idx="' + i + '">';
      }
      if (product.images.length > 1) {
        html += '<div class="ccs-ib-s2-pdp-carousel-dots">';
        for (var d = 0; d < product.images.length; d++) {
          html += '<button type="button" class="ccs-ib-s2-pdp-carousel-dot' + (d === 0 ? ' active' : '') + '" data-idx="' + d + '"></button>';
        }
        html += '</div>';
        html += '<button type="button" class="ccs-ib-s2-pdp-carousel-nav prev" data-dir="-1"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m15 18-6-6 6-6"/></svg></button>';
        html += '<button type="button" class="ccs-ib-s2-pdp-carousel-nav next" data-dir="1"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m9 18 6-6-6-6"/></svg></button>';
      }
      carousel.innerHTML = html;
      bindCarousel(carousel, product.images.length);
    }

    if (stock) {
      var variant = product.variants ? product.variants[0] : null;
      if (variant && variant.inventory_management === 'shopify' && variant.inventory_quantity > 0) {
        stock.innerHTML = '<div class="ccs-stock-pill ccs-stock-pill-green"><span class="ccs-stock-pip"></span>' + variant.inventory_quantity + ' in stock</div>';
      } else {
        stock.innerHTML = '<div class="ccs-stock-pill ccs-stock-pill-green"><span class="ccs-stock-pip"></span>In stock</div>';
      }
    }
  }

  function bindCarousel(container, count) {
    if (count <= 1) return;
    var current = 0;
    container.addEventListener('click', function (e) {
      var dot = e.target.closest('.ccs-ib-s2-pdp-carousel-dot');
      var nav = e.target.closest('.ccs-ib-s2-pdp-carousel-nav');
      if (dot) {
        current = parseInt(dot.getAttribute('data-idx'), 10);
      } else if (nav) {
        current = (current + parseInt(nav.getAttribute('data-dir'), 10) + count) % count;
      } else {
        return;
      }
      var imgs = container.querySelectorAll('img');
      var dots = container.querySelectorAll('.ccs-ib-s2-pdp-carousel-dot');
      for (var i = 0; i < imgs.length; i++) {
        imgs[i].style.display = i === current ? '' : 'none';
      }
      for (var j = 0; j < dots.length; j++) {
        dots[j].classList.toggle('active', j === current);
      }
    });
  }

  function updateSheetWithFullData(product) {
    var body = document.getElementById('ccs-ib-sheet-product-body');
    if (!body) return;
    var descEl = body.querySelector('.ccs-ib-s2-pdp-desc');
    if (descEl && product.body_html) descEl.innerHTML = product.body_html;
  }

  function addSelectedToList() {
    var wrap = document.querySelector('.ccs-ib-s2-card-wrap[data-handle="' + selectedHandle + '"]');
    if (!wrap) return;

    var qtyInput = document.getElementById('ccs-ib-s2-pdp-qty-input');
    if (window.innerWidth < 768) {
      var sheetQty = document.querySelector('#ccs-ib-sheet-product-body .ccs-ib-s2-sheet-qty');
      if (sheetQty) qtyInput = sheetQty;
    }
    var qty = parseInt(qtyInput ? qtyInput.value : '1', 10) || 1;

    var activeVariant = document.querySelector('.ccs-ib-s2-pdp-variant-btn.active');
    var variantId = activeVariant ? parseInt(activeVariant.getAttribute('data-variant-id'), 10) : parseInt(wrap.getAttribute('data-variant-id'), 10);
    var price = activeVariant ? parseInt(activeVariant.getAttribute('data-variant-price'), 10) : parseInt(wrap.getAttribute('data-price'), 10);

    var found = false;
    for (var i = 0; i < state.products.length; i++) {
      if (state.products[i].handle === selectedHandle) {
        state.products[i].quantity += qty;
        state.products[i].variantId = variantId;
        state.products[i].price = price;
        found = true;
        break;
      }
    }
    if (!found) {
      state.products.push({
        handle: selectedHandle,
        title: wrap.getAttribute('data-title'),
        variantId: variantId,
        price: price,
        quantity: qty,
        imageUrl: wrap.getAttribute('data-image'),
        category: wrap.getAttribute('data-product-type')
      });
    }

    wrap.classList.add('on-list');
    showAddToast(wrap.getAttribute('data-title') + ' added');
    updateDrawer();
    saveState();

    if (window.innerWidth < 768) {
      closeAllSheets();
    }

    track('product_added', { product_handle: selectedHandle, quantity: qty });
  }

  function updateDrawer() {
    var drawer = document.getElementById('ccs-ib-drawer');
    if (!drawer) return;

    var hasItems = state.products.length > 0;
    if (hasItems) {
      drawer.classList.add('has-items');
    } else {
      drawer.classList.remove('has-items', 'expanded');
    }

    /* Collapsed: thumbnails */
    var thumbsEl = document.getElementById('ccs-ib-drawer-thumbs');
    var moreEl = document.getElementById('ccs-ib-drawer-more');
    var totalEl = document.getElementById('ccs-ib-drawer-total');

    if (thumbsEl) {
      var thumbHtml = '';
      var showMax = Math.min(5, state.products.length);
      for (var t = 0; t < showMax; t++) {
        var imgUrl = state.products[t].imageUrl || '';
        thumbHtml += '<img class="ccs-ib-drawer-thumb" src="' + imgUrl + '" alt="' + (state.products[t].title || '').replace(/"/g, '&quot;') + '">';
      }
      thumbsEl.innerHTML = thumbHtml;
    }
    if (moreEl) {
      moreEl.textContent = state.products.length > 5 ? '+' + (state.products.length - 5) + ' more' : '';
    }

    var subtotal = 0;
    for (var s = 0; s < state.products.length; s++) {
      subtotal += state.products[s].price * state.products[s].quantity;
    }
    if (totalEl) totalEl.textContent = hasItems ? formatMoney(subtotal) : '';

    /* Expanded: item cards */
    var scrollEl = document.getElementById('ccs-ib-drawer-scroll');
    var countEl = document.getElementById('ccs-ib-drawer-count');


    if (countEl) countEl.textContent = '(' + state.products.length + ' items)';

    /* Footer: shipping progress + total */
    var shippingText = document.getElementById('ccs-ib-drawer-shipping-text');

    var footerTotal = document.getElementById('ccs-ib-drawer-footer-total');

    if (footerTotal) footerTotal.textContent = hasItems ? formatMoney(subtotal) : '';

    if (shippingText) {
      shippingText.innerHTML = '<span class="free">First order ships free</span> — code FREESHIP (up to $18, GTA)';
    }

    if (scrollEl) {
      var html = '';
      for (var i = 0; i < state.products.length; i++) {
        var p = state.products[i];
        var lineTotal = p.price * p.quantity;
        html += '<div class="ccs-ib-drawer-item" data-handle="' + p.handle + '">';
        html += '<img class="ccs-ib-drawer-item-img" src="' + (p.imageUrl || '') + '" alt="' + (p.title || '').replace(/"/g, '&quot;') + '">';
        html += '<div class="ccs-ib-drawer-item-title">' + (p.title || '') + '</div>';
        html += '<div class="ccs-ib-drawer-item-meta">' + p.quantity + ' x ' + formatMoney(p.price) + ' = <strong>' + formatMoney(lineTotal) + '</strong></div>';
        html += '<button type="button" class="ccs-ib-drawer-item-remove" data-handle="' + p.handle + '">Remove</button>';
        html += '</div>';
      }
      scrollEl.innerHTML = html;

      /* Bind remove buttons in expanded view */
      var removeBtns = scrollEl.querySelectorAll('.ccs-ib-drawer-item-remove');
      for (var r = 0; r < removeBtns.length; r++) {
        removeBtns[r].addEventListener('click', function (e) {
          e.stopPropagation();
          removeFromList(this.getAttribute('data-handle'));
        });
      }
    }
  }

  function toggleDrawer() {
    var drawer = document.getElementById('ccs-ib-drawer');
    if (!drawer) return;
    if (drawer.classList.contains('expanded')) {
      drawer.classList.remove('expanded');
    } else {
      drawer.classList.add('expanded');
    }
  }

  function removeFromList(handle) {
    for (var i = 0; i < state.products.length; i++) {
      if (state.products[i].handle === handle) {
        state.products.splice(i, 1);
        break;
      }
    }
    var wrap = document.querySelector('.ccs-ib-s2-card-wrap[data-handle="' + handle + '"]');
    if (wrap) wrap.classList.remove('on-list');
    saveState();
    updateDrawer();
    syncOnListBadges();
  }

  function syncOnListBadges() {
    var allCards = $$('.ccs-ib-s2-card-wrap');
    var onList = {};
    for (var i = 0; i < state.products.length; i++) {
      onList[state.products[i].handle] = true;
    }
    for (var j = 0; j < allCards.length; j++) {
      var handle = allCards[j].getAttribute('data-handle');
      if (onList[handle]) {
        allCards[j].classList.add('on-list');
      } else {
        allCards[j].classList.remove('on-list');
      }
    }
  }

  function showAddToast(msg) {
    var toast = document.getElementById('ccs-ib-add-toast');
    var msgEl = document.getElementById('ccs-ib-add-toast-msg');
    if (!toast || !msgEl) return;
    msgEl.textContent = msg;
    toast.classList.add('visible');
    if (toast._timer) clearTimeout(toast._timer);
    toast._timer = setTimeout(function () {
      toast.classList.remove('visible');
    }, 2500);
  }

  function goToReview() {
    if (state.products.length === 0) return;
    track('step_completed', { step_number: 2 });
    renderStep3();
    goToStep(3);
  }

  /* ─── Mobile Bottom Sheets ─────────────────────────────────── */

  function openProductSheet(data) {
    var overlay = document.getElementById('ccs-ib-sheet-overlay');
    var sheet = document.getElementById('ccs-ib-sheet-product');
    var body = document.getElementById('ccs-ib-sheet-product-body');
    if (!sheet || !body) return;

    var html = '';
    html += '<div class="ccs-ib-s2-pdp-carousel">';
    if (data.image) html += '<img src="' + data.image + '" alt="' + (data.title || '').replace(/"/g, '&amp;quot;') + '">';
    html += '</div>';
    html += '<div class="ccs-ib-s2-pdp-brand">' + (data.vendor || '') + '</div>';
    html += '<h3 class="ccs-ib-s2-pdp-title">' + (data.title || '') + '</h3>';
    var sheetFirstLabel = 'each';
    var sheetFirstPrice = data.price;
    if (data.variants && data.variants.length > 0) {
      sheetFirstPrice = parseInt(data.variants[0].price, 10) || data.price;
      if (data.variants[0].title && data.variants[0].title !== 'Default Title' && data.variants[0].title !== 'Default') {
        sheetFirstLabel = data.variants[0].title;
      }
    }
    html += '<div class="ccs-ib-s2-pdp-price">' + formatMoney(sheetFirstPrice) + ' <span class="unit">/ ' + sheetFirstLabel + '</span></div>';
    html += '<div class="ccs-ib-s2-pdp-stock"></div>';

    if (data.variants && data.variants.length > 1) {
      html += '<div class="ccs-ib-s2-pdp-variants">';
      for (var v = 0; v < data.variants.length; v++) {
        html += '<button type="button" class="ccs-ib-s2-pdp-variant-btn' + (v === 0 ? ' active' : '') + '" data-variant-id="' + data.variants[v].id + '" data-variant-price="' + data.variants[v].price + '">' + data.variants[v].title + '</button>';
      }
      html += '</div>';
    }

    html += '<div class="ccs-ib-s2-pdp-actions">';
    html += '<div class="ccs-ib-qty">';
    html += '<button type="button" class="ccs-ib-qty-btn ccs-ib-s2-sheet-dec">-</button>';
    html += '<input type="text" class="ccs-ib-qty-input ccs-ib-s2-sheet-qty" inputmode="numeric" value="1" min="1" max="99">';
    html += '<button type="button" class="ccs-ib-qty-btn ccs-ib-s2-sheet-inc">+</button>';
    html += '</div>';
    html += '<button type="button" class="ccs-ib-s2-pdp-atl ccs-ib-s2-sheet-atl">Add to List</button>';
    html += '</div>';
    html += '<div class="ccs-ib-s2-pdp-desc"><p>Loading details...</p></div>';

    body.innerHTML = html;

    var dec = body.querySelector('.ccs-ib-s2-sheet-dec');
    var inc = body.querySelector('.ccs-ib-s2-sheet-inc');
    var qtyInput = body.querySelector('.ccs-ib-s2-sheet-qty');
    if (dec) dec.addEventListener('click', function () { var v = parseInt(qtyInput.value, 10) || 1; if (v > 1) qtyInput.value = v - 1; });
    if (inc) inc.addEventListener('click', function () { var v = parseInt(qtyInput.value, 10) || 1; if (v < 99) qtyInput.value = v + 1; });

    var atl = body.querySelector('.ccs-ib-s2-sheet-atl');
    if (atl) atl.addEventListener('click', function () { addSelectedToList(); });

    var varBtns = body.querySelectorAll('.ccs-ib-s2-pdp-variant-btn');
    for (var b = 0; b < varBtns.length; b++) {
      varBtns[b].addEventListener('click', function () {
        for (var s = 0; s < varBtns.length; s++) varBtns[s].classList.remove('active');
        this.classList.add('active');
        /* Update price in sheet */
        var vPrice = parseInt(this.getAttribute('data-variant-price'), 10);
        var priceEl = body.querySelector('.ccs-ib-s2-pdp-price');
        if (priceEl) priceEl.innerHTML = formatMoney(vPrice) + ' <span class="unit">/ ' + this.textContent + '</span>';
      });
    }

    if (overlay) overlay.classList.add('open');
    sheet.classList.add('open');
  }

  function openListSheet() {
    var overlay = document.getElementById('ccs-ib-sheet-overlay');
    var sheet = document.getElementById('ccs-ib-sheet-list');
    var body = document.getElementById('ccs-ib-sheet-list-body');
    if (!sheet || !body) return;

    var html = '<h3 style="font-size:17px;font-weight:700;margin:0 0 16px;">Your List (' + state.products.length + ' items)</h3>';
    var subtotal = 0;
    for (var i = 0; i < state.products.length; i++) {
      var p = state.products[i];
      var lineTotal = p.price * p.quantity;
      subtotal += lineTotal;
      html += '<div style="display:flex;justify-content:space-between;padding:10px 0;border-bottom:1px solid var(--ccs-border-light);font-size:14px;">';
      html += '<span style="flex:1;">' + p.title + ' x' + p.quantity + '</span>';
      html += '<span style="font-weight:600;">' + formatMoney(lineTotal) + '</span>';
      html += '</div>';
    }
    html += '<div style="display:flex;justify-content:space-between;font-size:16px;font-weight:700;margin:16px 0;padding-top:12px;border-top:1px solid var(--ccs-border);">';
    html += '<span>Subtotal</span><span>' + formatMoney(subtotal) + '</span>';
    html += '</div>';
    html += '<button type="button" class="ccs-ib-cta" style="width:100%;justify-content:center;" onclick="window.ccsBuilderGoReview()">Continue</button>';

    body.innerHTML = html;
    if (overlay) overlay.classList.add('open');
    sheet.classList.add('open');
  }

  function closeAllSheets() {
    var overlay = document.getElementById('ccs-ib-sheet-overlay');
    var sheets = $$('.ccs-ib-sheet');
    if (overlay) overlay.classList.remove('open');
    for (var i = 0; i < sheets.length; i++) {
      sheets[i].classList.remove('open');
    }
  }

  window.ccsBuilderGoReview = function () {
    closeAllSheets();
    goToReview();
  };
  function renderStep3() {
    var listEl = document.getElementById('ccs-ib-s3-list');
    var emptyEl = document.getElementById('ccs-ib-s3-empty');
    var addMoreEl = document.getElementById('ccs-ib-s3-add-more');
    if (!listEl) return;

    if (state.products.length === 0) {
      listEl.innerHTML = '';
      if (emptyEl) emptyEl.style.display = '';
      if (addMoreEl) addMoreEl.style.display = 'none';
      updateStep3Pricing();
      return;
    }

    if (emptyEl) emptyEl.style.display = 'none';
    if (addMoreEl) addMoreEl.style.display = '';

    var html = '';
    for (var i = 0; i < state.products.length; i++) {
      var p = state.products[i];
      var lineTotal = p.price * p.quantity;
      html += '<div class="ccs-ib-s3-row" data-handle="' + p.handle + '">';
      html += '<img class="ccs-ib-s3-row-img" src="' + (p.imageUrl || '') + '" alt="' + (p.title || '').replace(/"/g, '&quot;') + '" width="48" height="48" loading="lazy">';
      html += '<div class="ccs-ib-s3-row-info">';
      html += '<div class="ccs-ib-s3-row-title">' + (p.title || '') + '</div>';
      html += '<div class="ccs-ib-s3-row-variant">' + formatMoney(p.price) + ' each</div>';
      html += '</div>';
      html += '<div class="ccs-ib-qty">';
      html += '<button type="button" class="ccs-ib-qty-btn ccs-ib-s3-dec" aria-label="Decrease">-</button>';
      html += '<input type="text" class="ccs-ib-qty-input ccs-ib-s3-qty" value="' + p.quantity + '" inputmode="numeric" aria-label="Quantity">';
      html += '<button type="button" class="ccs-ib-qty-btn ccs-ib-s3-inc" aria-label="Increase">+</button>';
      html += '</div>';
      html += '<span class="ccs-ib-s3-row-total">' + formatMoney(lineTotal) + '</span>';
      html += '<button type="button" class="ccs-ib-s3-row-remove" data-handle="' + p.handle + '" aria-label="Remove">';
      html += '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
      html += '</button>';
      html += '</div>';
    }
    listEl.innerHTML = html;

    /* Bind qty steppers and remove buttons */
    var rows = listEl.querySelectorAll('.ccs-ib-s3-row');
    for (var r = 0; r < rows.length; r++) {
      (function (row) {
        var handle = row.getAttribute('data-handle');
        var qtyInput = row.querySelector('.ccs-ib-s3-qty');
        var decBtn = row.querySelector('.ccs-ib-s3-dec');
        var incBtn = row.querySelector('.ccs-ib-s3-inc');
        var totalEl = row.querySelector('.ccs-ib-s3-row-total');
        var removeBtn = row.querySelector('.ccs-ib-s3-row-remove');

        function updateRowQty(newVal) {
          var val = Math.max(0, Math.min(99, newVal));
          if (val === 0) {
            removeFromList(handle);
            row.remove();
            if (state.products.length === 0 && emptyEl) {
              emptyEl.style.display = '';
              if (addMoreEl) addMoreEl.style.display = 'none';
            }
            updateStep3Pricing();
            return;
          }
          qtyInput.value = val;
          for (var j = 0; j < state.products.length; j++) {
            if (state.products[j].handle === handle) {
              state.products[j].quantity = val;
              totalEl.textContent = formatMoney(state.products[j].price * val);
              break;
            }
          }
          updateStep3Pricing();
          saveState();
        }

        decBtn.addEventListener('click', function () {
          updateRowQty(parseInt(qtyInput.value, 10) - 1);
        });
        incBtn.addEventListener('click', function () {
          updateRowQty(parseInt(qtyInput.value, 10) + 1);
        });
        qtyInput.addEventListener('change', function () {
          updateRowQty(parseInt(this.value, 10) || 0);
        });
        removeBtn.addEventListener('click', function () {
          removeFromList(handle);
          row.remove();
          if (state.products.length === 0 && emptyEl) {
            emptyEl.style.display = '';
            if (addMoreEl) addMoreEl.style.display = 'none';
          }
          updateStep3Pricing();
        });
      })(rows[r]);
    }

    /* Update delivery info */
    var delivery = getDeliveryDay(state.postalCode);
    var locationEl = document.getElementById('ccs-ib-del-location');
    var dayEl = document.getElementById('ccs-ib-del-day');
    var dateEl = document.getElementById('ccs-ib-del-date');
    var cutoffEl = document.getElementById('ccs-ib-del-cutoff');
    if (locationEl) locationEl.textContent = state.postalCode ? state.postalCode.substring(0, 3).toUpperCase() + ' area (GTA)' : 'GTA';
    if (dayEl) dayEl.textContent = delivery.day;
    if (dateEl) dateEl.textContent = getNextDeliveryDate(delivery.day);
    if (cutoffEl) cutoffEl.textContent = delivery.cutoff;
    updateDeliveryDate();

    updateStep3Pricing();
  }

  function updateStep3Pricing() {
    var subtotal = getSubtotal();
    var tax = Math.round(subtotal * HST_RATE);
    var total = subtotal + tax;

    var pricingEl = document.getElementById('ccs-ib-s3-pricing');
    if (!pricingEl) return;

    var html = '';
    html += '<div class="ccs-ib-s3-pricing-row"><span>Subtotal</span><span>' + formatMoney(subtotal) + '</span></div>';
    html += '<div class="ccs-ib-s3-pricing-row"><span>Delivery</span><span>Calculated at checkout</span></div>';
    html += '<div class="ccs-ib-s3-pricing-row"><span>HST (13%)</span><span>' + formatMoney(tax) + '</span></div>';
    html += '<div class="ccs-ib-s3-pricing-row total"><span>Total</span><span>' + formatMoney(total) + '</span></div>';

    pricingEl.innerHTML = html;
  }

  function updateDeliveryDate() {
    var delivery = getDeliveryDay(state.postalCode);
    var dateEl = document.getElementById('ccs-ib-del-date');
    var dayEl = document.getElementById('ccs-ib-del-day');
    if (!dateEl) return;

    var freq = state.frequency || 'weekly';
    var nextDate = getNextDeliveryDate(delivery.day);

    if (freq === 'onetime') {
      if (dayEl) dayEl.textContent = delivery.day;
      dateEl.textContent = nextDate;
    } else if (freq === 'weekly') {
      if (dayEl) dayEl.textContent = delivery.day;
      dateEl.textContent = nextDate;
    } else if (freq === 'biweekly') {
      if (dayEl) dayEl.textContent = delivery.day;
      dateEl.textContent = nextDate + ' (then every 2 weeks)';
    } else if (freq === 'monthly') {
      if (dayEl) dayEl.textContent = delivery.day;
      dateEl.textContent = nextDate + ' (then monthly)';
    }
  }

  function initStep3() {
    /* Frequency selector */
    var freqCards = $$('.ccs-ib-freq-card');
    for (var f = 0; f < freqCards.length; f++) {
      freqCards[f].addEventListener('click', function () {
        for (var i = 0; i < freqCards.length; i++) {
          freqCards[i].setAttribute('aria-checked', 'false');
        }
        this.setAttribute('aria-checked', 'true');
        state.frequency = this.getAttribute('data-freq');
        track('frequency_selected', { frequency: state.frequency });
        updateDeliveryDate();
        saveState();
      });
    }

    /* Add More Items button */
    var addMoreBtn = document.getElementById('ccs-ib-s3-add-more');
    if (addMoreBtn) {
      addMoreBtn.addEventListener('click', function () {
        syncOnListBadges();
        updateDrawer();
        resetPanel();
        goToStep(2);
      });
    }

    /* Continue button */
    var continueBtn = document.getElementById('ccs-ib-s3-continue');
    if (continueBtn) {
      continueBtn.addEventListener('click', function () {
        track('step_completed', { step_number: 3 });
        renderStep4Summary();
        goToStep(4);
      });
    }
  }

  function renderStep4Summary() {
    var container = document.getElementById('ccs-ib-s4-summary-content');
    if (!container) return;

    var subtotal = getSubtotal();
    var tax = Math.round(subtotal * HST_RATE);
    var total = subtotal + tax;
    var delivery = getDeliveryDay(state.postalCode);

    var html = '<div class="ccs-ib-s4-items">';
    for (var i = 0; i < state.products.length; i++) {
      var p = state.products[i];
      var hiddenClass = i >= 3 ? ' ccs-ib-s4-item-hidden' : '';
      html += '<div class="ccs-ib-s4-item' + hiddenClass + '"><span class="ccs-ib-s4-item-name">' + p.title + ' x' + p.quantity + '</span><span class="ccs-ib-s4-item-total">' + formatMoney(p.price * p.quantity) + '</span></div>';
    }
    html += '</div>';
    if (state.products.length > 3) {
      html += '<div class="ccs-ib-s4-more" id="ccs-ib-s4-more">+ ' + (state.products.length - 3) + ' more items</div>';
    }

    html += '<div class="ccs-ib-s3-pricing-row"><span>Subtotal</span><span>' + formatMoney(subtotal) + '</span></div>';
    html += '<div class="ccs-ib-s3-pricing-row"><span>Delivery</span><span>Calculated at checkout</span></div>';
    html += '<div class="ccs-ib-s3-pricing-row"><span>HST (13%)</span><span>' + formatMoney(tax) + '</span></div>';
    html += '<div class="ccs-ib-s3-pricing-row total"><span>Total</span><span>' + formatMoney(total) + '</span></div>';
    html += '<div style="margin-top:12px;font-size:13px;color:var(--ccs-text-3);">';
    html += 'Delivery: ' + (state.frequency === 'onetime' ? 'One-time' : state.frequency.charAt(0).toUpperCase() + state.frequency.slice(1));
    html += ' <a href="#" style="color:var(--ccs-blue-link);" onclick="window.ccsBuilderGoReview();return false;">Edit</a>';
    html += '</div>';

    container.innerHTML = html;

    /* Bind "more items" toggle */
    var moreBtn = document.getElementById('ccs-ib-s4-more');
    if (moreBtn) {
      moreBtn.addEventListener('click', function () {
        var hiddenItems = container.querySelectorAll('.ccs-ib-s4-item-hidden');
        for (var h = 0; h < hiddenItems.length; h++) {
          hiddenItems[h].classList.remove('ccs-ib-s4-item-hidden');
        }
        this.style.display = 'none';
      });
    }

    /* Pre-fill email if captured earlier */
    var emailField = document.getElementById('ccs-ib-email');
    if (emailField && state.email && !emailField.value) {
      emailField.value = state.email;
    }
  }

  function validateHst(val) {
    if (!val) return true; /* optional */
    var normalized = val.replace(/[\s-]/g, '').toUpperCase();
    return /^\d{9}RT\d{4}$/.test(normalized);
  }

  function validateStep4() {
    var bizName = document.getElementById('ccs-ib-biz-name').value.trim();
    var contactName = document.getElementById('ccs-ib-contact-name').value.trim();
    var email = document.getElementById('ccs-ib-email').value.trim();
    var address = document.getElementById('ccs-ib-address').value.trim();
    var hstRaw = document.getElementById('ccs-ib-hst').value.trim();
    var hst = hstRaw ? hstRaw.replace(/[\s-]/g, '').toUpperCase() : '';

    var hasError = false;
    var fields = [
      { id: 'ccs-ib-biz-name', val: bizName, required: true },
      { id: 'ccs-ib-contact-name', val: contactName, required: true },
      { id: 'ccs-ib-email', val: email, required: true, pattern: /\S+@\S+\.\S+/ },
      { id: 'ccs-ib-address', val: address, required: true }
    ];

    for (var i = 0; i < fields.length; i++) {
      var f = fields[i];
      var fieldEl = document.getElementById(f.id).closest('.ccs-ib-field');
      if (f.required && !f.val) {
        fieldEl.classList.add('has-error');
        if (!hasError) document.getElementById(f.id).focus();
        hasError = true;
      } else if (f.pattern && !f.pattern.test(f.val)) {
        fieldEl.classList.add('has-error');
        if (!hasError) document.getElementById(f.id).focus();
        hasError = true;
      } else {
        fieldEl.classList.remove('has-error');
      }
    }

    if (hst && !validateHst(hst)) {
      var hstField = document.getElementById('ccs-ib-hst').closest('.ccs-ib-field');
      hstField.classList.add('has-error');
      if (!hasError) document.getElementById('ccs-ib-hst').focus();
      hasError = true;
    }

    return !hasError;
  }

  function getStep4FormData() {
    return {
      bizName: document.getElementById('ccs-ib-biz-name').value.trim(),
      contactName: document.getElementById('ccs-ib-contact-name').value.trim(),
      email: document.getElementById('ccs-ib-email').value.trim(),
      phone: document.getElementById('ccs-ib-phone').value.trim(),
      address: document.getElementById('ccs-ib-address').value.trim(),
      suite: document.getElementById('ccs-ib-suite').value.trim(),
      instructions: document.getElementById('ccs-ib-instructions').value.trim(),
      hst: document.getElementById('ccs-ib-hst').value.trim().replace(/[\s-]/g, '').toUpperCase()
    };
  }

  /* ── Main flow: Add to cart → Shopify checkout ── */
  function submitToCheckout() {
    if (!validateStep4()) return;

    var loading = document.getElementById('ccs-ib-loading');
    if (loading) loading.classList.add('visible');

    var formData = getStep4FormData();
    var freqLabel = state.frequency === 'onetime' ? 'One-time' : state.frequency.charAt(0).toUpperCase() + state.frequency.slice(1);

    /* Step 0: Check the existing cart — never wipe it silently */
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        if (cart && cart.item_count > 0) {
          var replaceOk = window.confirm('Your cart has ' + cart.item_count + ' item' + (cart.item_count === 1 ? '' : 's') + ' in it. Replace them with your list?');
          if (!replaceOk) {
            var cancelErr = new Error('cart-replace-cancelled');
            cancelErr.ccsCancelled = true;
            throw cancelErr;
          }
        }
        /* Step 1: Clear the cart */
        return fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      })
      .then(function () {
        /* Step 2: Add all products to cart.
           EXCEPTION to the canonical-ATC rule (snippets/ccs-atc.liquid):
           this 3-step chain (clear → add many → set attributes → redirect
           to /checkout) owns its own progress UI + submit gating, so raw
           fetch is intentional. The chain redirects to /checkout
           immediately on success, so opening the side drawer mid-flow
           would be wrong UX. */
        var items = [];
        for (var i = 0; i < state.products.length; i++) {
          items.push({
            id: state.products[i].variantId,
            quantity: state.products[i].quantity
          });
        }
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items })
        });
      })
      .then(function () {
        /* Step 3: Set cart attributes with B2B info */
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            note: 'Builder order — ' + freqLabel + ' delivery',
            attributes: {
              'Business Name': formData.bizName,
              'Contact Name': formData.contactName,
              'Facility Type': state.facilityType,
              'Delivery Frequency': freqLabel,
              'Delivery Instructions': formData.instructions || 'None',
              'Suite/Unit': formData.suite || 'N/A',
              'HST Number': formData.hst || 'Not provided',
              'Postal Code': state.postalCode,
              'Source': 'Inventory Builder'
            }
          })
        });
      })
      .then(function () {
        /* Step 4: Track and redirect to checkout */
        state.submitted = true;
        track('builder_submitted', {
          item_count: state.products.length,
          subtotal: getSubtotal(),
          frequency: state.frequency
        });
        clearState();
        window.location.href = '/checkout';
      })
      .catch(function (err) {
        if (loading) loading.classList.remove('visible');
        if (err && err.ccsCancelled) return;
        alert('Something went wrong adding items to cart. Please try again.');
      });
  }

  /* ── Quote request: Send to /contact ── */
  function submitQuoteRequest() {
    if (!validateStep4()) return;

    var loading = document.getElementById('ccs-ib-loading');
    if (loading) loading.classList.add('visible');

    var formData = getStep4FormData();
    var subtotal = getSubtotal();
    var tax = Math.round(subtotal * HST_RATE);

    var payload = JSON.stringify({
      type: 'quote_request',
      business_name: formData.bizName,
      contact_name: formData.contactName,
      email: formData.email,
      phone: formData.phone,
      delivery_address: formData.address,
      suite: formData.suite,
      delivery_instructions: formData.instructions,
      hst_number: formData.hst,
      postal_code: state.postalCode,
      facility_type: state.facilityType,
      delivery_frequency: state.frequency,
      products: state.products.map(function (p) {
        return {
          handle: p.handle, name: p.title, variantId: p.variantId,
          quantity: p.quantity, price: p.price, lineTotal: p.price * p.quantity
        };
      }),
      subtotal: subtotal,
      tax: tax,
      total: subtotal + tax,
      source: getUtmSource()
    });

    var body = new URLSearchParams({
      'form_type': 'contact',
      'contact[email]': formData.email,
      'contact[body]': payload,
      'contact[tags]': 'builder,quote-request'
    });

    fetch('/contact', { method: 'POST', body: body })
      .then(function () {
        if (loading) loading.classList.remove('visible');
        state.submitted = true;
        track('quote_requested', {
          item_count: state.products.length,
          subtotal: subtotal,
          frequency: state.frequency
        });

        /* Show confirmation */
        var delivery = getDeliveryDay(state.postalCode);
        var confirmSub = document.getElementById('ccs-ib-confirm-sub');
        if (confirmSub) {
          confirmSub.textContent = 'We received your list. We\'ll come back with your price at ' + formData.email + '.';
        }
        var confirmCard = document.getElementById('ccs-ib-confirm-card');
        if (confirmCard) {
          var html = '';
          html += '<div class="ccs-ib-confirm-card-row"><span class="label">Business</span><span class="value">' + formData.bizName + '</span></div>';
          html += '<div class="ccs-ib-confirm-card-row"><span class="label">Items</span><span class="value">' + state.products.length + ' products</span></div>';
          html += '<div class="ccs-ib-confirm-card-row"><span class="label">Estimated total</span><span class="value">' + formatMoney(subtotal + tax) + '</span></div>';
          html += '<div class="ccs-ib-confirm-card-row"><span class="label">Delivery</span><span class="value">Toronto &amp; GTA</span></div>';
          confirmCard.innerHTML = html;
        }
        clearState();
        goToStep('confirm');
      })
      .catch(function () {
        if (loading) loading.classList.remove('visible');
        alert('Something went wrong. Please try again or contact us directly.');
      });
  }

  function initStep4() {
    var form = document.getElementById('ccs-ib-form');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        submitToCheckout();
      });
    }

    var quoteLink = document.getElementById('ccs-ib-quote-link');
    if (quoteLink) {
      quoteLink.addEventListener('click', function (e) {
        e.preventDefault();
        submitQuoteRequest();
      });
    }

    var saveLink = document.getElementById('ccs-ib-save-link');
    if (saveLink) {
      saveLink.addEventListener('click', function (e) {
        e.preventDefault();
        saveState();
        track('list_saved', { step_number: 4, item_count: state.products.length });
        showAddToast('List saved! Come back anytime to pick up where you left off.');
      });
    }

    /* Copy referral link */
    var copyBtn = document.getElementById('ccs-ib-copy-referral');
    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        var url = window.location.origin + '/pages/build-your-list?ref=referral';
        if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(function () {
            copyBtn.textContent = 'Copied!';
            setTimeout(function () { copyBtn.textContent = 'Copy referral link'; }, 2000);
          });
        }
      });
    }
  }
  function showResumePrompt(saved) {
    var age = Date.now() - saved.timestamp;
    var hours = Math.floor(age / 3600000);
    var days = Math.floor(hours / 24);
    var timeAgo = days > 0 ? days + ' day' + (days > 1 ? 's' : '') + ' ago' : hours + ' hour' + (hours > 1 ? 's' : '') + ' ago';

    var overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.4);z-index:300;display:flex;align-items:center;justify-content:center;';
    var modal = document.createElement('div');
    modal.style.cssText = 'background:#fff;border-radius:12px;padding:32px;max-width:400px;text-align:center;';
    modal.innerHTML = '<h3 style="margin:0 0 8px;font-size:20px;font-weight:700;">Resume your list?</h3>' +
      '<p style="color:var(--ccs-text-3);margin:0 0 20px;font-size:15px;">You started a list ' + timeAgo + ' with ' + (saved.products ? saved.products.length : 0) + ' items.</p>' +
      '<div style="display:flex;gap:12px;justify-content:center;">' +
      '<button id="ccs-ib-resume" style="padding:10px 24px;background:var(--ccs-blue);color:#fff;border:none;border-radius:8px;font-weight:600;cursor:pointer;">Resume</button>' +
      '<button id="ccs-ib-fresh" style="padding:10px 24px;background:#fff;border:2px solid var(--ccs-border);border-radius:8px;font-weight:600;cursor:pointer;color:var(--ccs-text-2);">Start Fresh</button>' +
      '</div>';

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    document.getElementById('ccs-ib-resume').addEventListener('click', function () {
      /* Restore state */
      state.postalCode = saved.postalCode || '';
      state.facilityType = saved.facilityType || '';
      state.facilitySize = saved.facilitySize || '';
      state.bscSubTypes = saved.bscSubTypes || [];
      state.email = saved.email || '';
      state.caslConsent = saved.caslConsent || false;
      state.frequency = saved.frequency || 'weekly';

      /* Rebuild products from saved handles + DOM card data attributes */
      if (saved.products && saved.products.length > 0) {
        state.products = [];
        var cardDataMap = {};
        var allCards = $$('.ccs-ib-s2-card-wrap');
        for (var i = 0; i < allCards.length; i++) {
          var c = allCards[i];
          cardDataMap[c.getAttribute('data-handle')] = {
            handle: c.getAttribute('data-handle'),
            title: c.getAttribute('data-title'),
            variantId: parseInt(c.getAttribute('data-variant-id'), 10),
            price: parseInt(c.getAttribute('data-price'), 10),
            imageUrl: c.getAttribute('data-image'),
            category: c.getAttribute('data-product-type')
          };
        }
        for (var j = 0; j < saved.products.length; j++) {
          var sp = saved.products[j];
          var cardData = cardDataMap[sp.handle];
          if (cardData) {
            state.products.push({
              handle: cardData.handle, title: cardData.title, variantId: sp.variantId || cardData.variantId,
              price: cardData.price, quantity: sp.quantity, imageUrl: cardData.imageUrl,
              category: cardData.category
            });
          }
        }
      }

      overlay.remove();
      track('builder_resumed', { days_since_start: Math.floor((Date.now() - saved.timestamp) / 86400000) });

      if (state.products.length > 0) {
        sortRecommended();
        updateDrawer();
        resetPanel();
        syncOnListBadges();
        goToStep(saved.step > 1 ? saved.step : 2);
      }
    });

    document.getElementById('ccs-ib-fresh').addEventListener('click', function () {
      clearState();
      overlay.remove();
    });
  }

  /* ─── Boot ──────────────────────────────────────────────────── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
